<template>
  <div class="container" id="page-modal-parent">
    <div class="header">
      <div class="title uk-text-bold uk-text-large">{{ title }}</div>
      <HelpIcon v-if="helpInfoTitle" :tooltipText="helpInfoTitle" position="top-left" cls="uk-active uk-width-auto" />
      <p v-if="departmentPath" class="sub-title">部署：{{ departmentPath }}</p>
      <p v-if="statusName" class="sub-title">ステータス：{{ statusName }}</p>
      <ActionButton v-if="backable" class="back-button" title="戻る" shape="round" width="120" @click="$router.go(-1)" />
      <div class="action">
        <slot name="action" />
      </div>
    </div>
    <slot name="content" />
    <div
      ref="instructionModal"
      class="instruction-wrapper"
      uk-modal="bg-close: false;stack: true;"
      container="#page-modal-parent"
    >
      <div
        class="uk-modal-dialog uk-modal-body uk-width-2xlarge"
        v-if="instruction.message && ['confirm', 'alert'].includes(instruction.level)"
      >
        <div
          class="instruction"
          uk-alert
          :class="{
            'uk-alert-warning': instruction.level === 'confirm',
            'uk-alert-danger': instruction.level === 'alert'
          }"
        >
          <p v-html="sanitize(instruction.message.replace(/\n/g, '<br/>'))"></p>
          <p class="uk-text-right">
            <button
              class="uk-button uk-button-default uk-modal-close"
              type="button"
              v-if="instruction.level === 'confirm'"
              @click="clearInstruction"
            >
              キャンセル
            </button>
            <button class="uk-button uk-button-primary" type="button" @click="onConfirmed">
              OK
            </button>
          </p>
        </div>
      </div>
    </div>
    <div
      class="instruction-wrapper"
      :class="instruction.level"
      v-if="instruction.message && ['info', 'danger'].includes(instruction.level)"
    >
      <div
        ref="instructionAlert"
        class="instruction"
        uk-alert
        :class="{
          'uk-alert-primary': instruction.level === 'info',
          'uk-alert-danger': instruction.level === 'danger'
        }"
      >
        <a class="uk-alert-close" uk-close @click="onAlertCloseButtonClick"></a>
        <p v-html="sanitize(instruction.message.replace(/\n/g, '<br/>'))"></p>
      </div>
    </div>
    <div class="uk-flex-top" uk-modal="esc-close: false; bg-close: false" ref="loadingModal">
      <div class="uk-modal-dialog uk-modal-body loading" data-uk-modal="{center:true}">
        <div class="uk-position-center">
          <p class="message" v-html="loadingMessage" />
          <div class="loader"></div>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.container {
  color: $brand-color;
  position: relative;
}
.header {
  display: flex;
  align-items: center;
  margin-bottom: $spacing-ex-small;
}
.sub-title {
  margin: 0 0 0 20px;
}
.action {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}
[uk-modal].instruction-wrapper .uk-modal-body {
  padding: 0;
  .uk-button {
    margin-left: $spacing-medium;
    min-width: 120px;
  }
}
.instruction-wrapper.info {
  position: absolute;
  top: -$spacing-small;
  right: 0;
  width: 70%;
  z-index: 97;
}
.back-button {
  margin-left: $spacing-medium;
}
.loading {
  min-width: 20%;
  min-height: 300px;
  width: 350px;
  background-color: rgba(230, 230, 230, 0.9);
  .message {
    color: rgba(80, 80, 80);
    text-align: center;
    margin: 30px 0 0 0;
  }
}

// loading style
.loader,
.loader:after {
  border-radius: 50%;
  width: 10em;
  height: 10em;
}
.loader {
  margin: 60px auto;
  font-size: 10px;
  position: relative;
  text-indent: -9999em;
  border-top: 1.1em solid rgba(253, 252, 252, 0.2);
  border-right: 1.1em solid rgba(253, 252, 252, 0.2);
  border-bottom: 1.1em solid rgba(253, 252, 252, 0.2);
  border-left: 1.1em solid #676767;
  -webkit-transform: translateZ(0);
  -ms-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-animation: load8 1.1s infinite linear;
  animation: load8 1.1s infinite linear;
}
@-webkit-keyframes load8 {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@keyframes load8 {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
</style>
<script lang="ts">
import { Vue, Options } from "vue-class-component";
import { Prop, Watch, Ref } from "vue-property-decorator";
import Store from "../../store";
import ActionButton from "../atoms/ActionButton.vue";
import HelpIcon from "../atoms/HelpIcon.vue";
import { InstructionLevel } from "../../types";
import UIkit from "uikit";
import sanitizeHTML from "sanitize-html";
const storeInstance = Store.getInstance();

@Options({
  components: { ActionButton, HelpIcon }
})
export default class Page extends Vue {
  @Prop({ type: String }) title = "";
  @Prop({ type: Boolean }) backable = false;
  @Ref("instructionModal") instructionModal!: HTMLElement;
  @Prop({ type: String }) loadingMessage?: string = "";
  @Ref("loadingModal") loadingModal!: HTMLElement;
  @Prop({ type: String }) helpInfoTitle = "";
  @Prop({ type: String }) departmentPath = "";
  @Prop({ type: String }) statusName = "";
  instruction: {
    level: InstructionLevel;
    message: string;
    callback?: Function;
  } = storeInstance.getInstruction();
  isLoading: { state: boolean; message: string } = storeInstance.getIsLoading();
  confirmation = false;
  currentTimeout!: ReturnType<typeof setTimeout>;
  clearInstruction() {
    Store.getInstance().setInstructionContent(InstructionLevel.None, "");
  }
  onAlertCloseButtonClick() {
    this.clearInstruction();
  }
  sanitize(html: string) {
    return sanitizeHTML(html);
  }
  @Watch("instruction.level")
  onInstructionMessageChange(newValue: string) {
    if (newValue === "info") {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = setTimeout(() => {
        if (this.instruction.level === InstructionLevel.Info) {
          // @ts-ignore
          UIkit.util.once(this.$refs.instructionAlert as HTMLElement, "hide", () => {
            this.clearInstruction();
          });
          UIkit.alert(this.$refs.instructionAlert as HTMLElement).close();
        }
      }, 5000);
    } else if ([InstructionLevel.Confirm, InstructionLevel.Alert].includes(newValue as InstructionLevel)) {
      this.$nextTick(() => {
        UIkit.modal(this.instructionModal).show();
        // @ts-ignore
        UIkit.util.once(this.instructionModal, "hide", () => {
          if (this.confirmation) {
            if (this.instruction.callback) {
              this.instruction.callback.call(this);
            }
            this.clearInstruction();
            this.confirmation = false;
          }
        });
      });
    }
  }
  @Watch("isLoading.state")
  changeLoadingStatus(flg: boolean) {
    if (flg) {
      this.loadingMessage = this.isLoading.message;
      UIkit.modal(this.loadingModal).show();
    } else {
      UIkit.modal(this.loadingModal).hide();
    }
  }
  onConfirmed() {
    this.confirmation = true;
    UIkit.modal(this.instructionModal).hide();
    this.$emit("confirmed");
  }
  beforeUnmount() {
    if (this.instruction.level === InstructionLevel.Info) {
      // @ts-ignore
      UIkit.util.once(this.$refs.instructionAlert as HTMLElement, "hide", () => {
        this.clearInstruction();
      });
      UIkit.alert(this.$refs.instructionAlert as HTMLElement).close();
    }
  }
}
</script>
